<template>
  <div class="cat-mapeo-erp">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-mapeo-erp-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${mapeoErps.length} Mapeos`"
          @new="addMapeoErp"
          @refresh="loadMapeoErps"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando Mapeos ERP...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!mapeoErps || mapeoErps.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay Mapeos ERP registrados. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && mapeoErps && mapeoErps.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="mapeoErps"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'ErpId', dir: 'asc' }"
            filter-placeholder="Buscar Mapeos ERP..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editMapeoErp(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar Mapeo ERP"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.ErpId, row.ObjetoId, row.CampoGestion360)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar Mapeo ERP"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Mapeo ERP -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="mapeoErpModal" 
      tabindex="-1" 
      aria-labelledby="mapeoErpModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="mapeoErpModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Mapeo ERP' : 'Nuevo Mapeo ERP' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatMapeoErpForm
              :mapeoErp="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatMapeoErpService from '../controllers/CatMapeoErpController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatMapeoErpForm from '../components/CatMapeoErpForm.vue';

export default {
  name: 'CatMapeoErpView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatMapeoErpForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: new CatMapeoErpService(),
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        ErpId: '',
        ObjetoId: null,
        CampoGestion360: '',
        CampoErp: '',
        Transformacion: ''
      }
    };
  },
  computed: {
    mapeoErps() {
      return this.service.mapeoErps;
    },
    loading() {
      return this.service.loading;
    },
    error() {
      return this.service.error;
    },
    hasEmpresa() {
      const emp = JSON.parse(localStorage.getItem('empresa_seleccionada') || 'null');
      return !!(emp && emp.EmpresaId);
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'ErpId', label: 'ERP ID', sortable: true },
        { key: 'ObjetoId', label: 'Objeto ID', sortable: true },
        { key: 'CampoGestion360', label: 'Campo Gestión 360', sortable: true },
        { key: 'CampoErp', label: 'Campo ERP', sortable: true },
        { key: 'Transformacion', label: 'Transformación', sortable: true }
      ];
    }
  },
  created() {
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadMapeoErps() {
      console.log('Cargando Mapeos ERP...');
      await this.service.listar();
    },
    addMapeoErp() {
      this.isEditing = false;
      this.openModal();
    },
    async editMapeoErp(mapeoErp) {
      this.isEditing = true;
      try {
        // Cargar datos completos del Mapeo ERP
        const response = await this.service.lectura(mapeoErp.ErpId, mapeoErp.ObjetoId, mapeoErp.CampoGestion360);
        if (response && response.data) {
          this.formData = { ...response.data };
        } else {
          this.formData = { ...mapeoErp };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar Mapeo ERP:', error);
        this.$alert.error('No se pudo cargar el Mapeo ERP para edición.');
      }
    },
    confirmDelete(erpId, objetoId, campoGestion360) {
      console.log('Iniciando confirmación de eliminación para:', erpId, objetoId, campoGestion360);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el Mapeo ERP "${erpId}" - "${campoGestion360}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteMapeoErp(erpId, objetoId, campoGestion360);
        }
      });
    },
    async deleteMapeoErp(erpId, objetoId, campoGestion360) {
      console.log('Ejecutando eliminación para:', erpId, objetoId, campoGestion360);
      
      try {
        const response = await this.service.borrar({ 
          ErpId: erpId, 
          ObjetoId: objetoId, 
          CampoGestion360: campoGestion360 
        });
        console.log('Respuesta de eliminación recibida:', response);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Mapeo ERP eliminado exitosamente');
          // Recargar datos
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo eliminar el Mapeo ERP. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar Mapeo ERP:', error);
        this.$alert.error('No se pudo eliminar el Mapeo ERP. Por favor, intente nuevamente.');
      }
    },
    async downloadBulk() {
      try {
        await this.service.downloadBulk();
        this.$alert.success('Descarga iniciada correctamente');
      } catch (error) {
        this.$alert.error('Error al descargar: ' + error.message);
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        ErpId: '',
        ObjetoId: null,
        CampoGestion360: '',
        CampoErp: '',
        Transformacion: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        const response = await this.service.guardar(formData);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Mapeo ERP guardado exitosamente');
          // Cerrar modal y recargar datos
          this.closeModal();
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo guardar el Mapeo ERP. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al guardar Mapeo ERP:', error);
        this.$alert.error('No se pudo guardar el Mapeo ERP. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.cat-mapeo-erp {
  min-height: calc(100vh - 200px);
}

.cat-mapeo-erp-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
